import React, { useState, useCallback, useEffect } from 'react';
import { ChevronDownIcon, CheckIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useOrganization } from '../../context/OrganizationContext';
import Badge from './Badge';
import Dropdown from './Dropdown';
import LoadingSpinner from './LoadingSpinner';
import { CreateNewOrganizationModal } from '../organization/CreateNewOrganizationModal';

interface OrganizationSwitcherProps {
  isCollapsed: boolean;
}

const OrganizationSwitcher: React.FC<OrganizationSwitcherProps> = ({ isCollapsed }) => {
  const { 
    organization, 
    role, 
    userOrganizations, 
    loading, 
    switchingOrganization,
    loadUserOrganizations,
    switchOrganization
  } = useOrganization();
  
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Load user organizations when component mounts
  useEffect(() => {
    const loadData = async () => {
      try {
        await loadUserOrganizations();
      } catch (error) {
        console.error('[OrganizationSwitcher] Failed to load user organizations:', error);
      }
    };
    loadData();
  }, [loadUserOrganizations]);

  // Helper functions for role handling
  const getRoleBadgeVariant = (userRole: string) => {
    switch (userRole) {
      case 'owner':
        return 'primary';
      case 'admin':
        return 'info';
      case 'member':
        return 'success';
      default:
        return 'secondary';
    }
  };

  const getRoleDisplayName = (userRole: string) => {
    switch (userRole) {
      case 'owner':
        return 'Owner';
      case 'admin':
        return 'Admin';
      case 'member':
        return 'Member';
      default:
        return 'Unknown';
    }
  };

  // Handle organization switch
  const handleSwitchOrganization = useCallback(async (organizationId: string) => {
    try {
      await switchOrganization(organizationId);
      setDropdownOpen(false);
    } catch (error) {
      console.error('[OrganizationSwitcher] Failed to switch organization:', error);
      // Error is handled by context, just keep dropdown open for retry
    }
  }, [switchOrganization]);

  // Toggle dropdown
  const toggleDropdown = useCallback(() => {
    if (!switchingOrganization) {
      setDropdownOpen(prev => !prev);
    }
  }, [switchingOrganization]);

  // Close dropdown
  const closeDropdown = useCallback(() => {
    setDropdownOpen(false);
  }, []);

  // Handle create new organization
  const handleCreateNewOrganization = useCallback(() => {
    setDropdownOpen(false);
    setShowCreateModal(true);
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  // Handle successful organization creation
  const handleCreateSuccess = useCallback((organizationName: string) => {
    setShowCreateModal(false);
    // The modal already handles switching to the new organization
    console.log(`Successfully created and switched to organization: ${organizationName}`);
  }, []);

  // Create dropdown items
  const organizationItems = userOrganizations.map(membership => ({
    key: membership.organization.id,
    label: (
      <div className="flex items-center justify-between w-full min-w-0">
        <div className="flex items-center min-w-0 flex-1 mr-2">
          <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center mr-3 flex-shrink-0">
            <span className="text-white font-semibold text-xs">
              {membership.organization.displayName.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 truncate">
              {membership.organization.displayName}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {membership.organization.name}
            </p>
          </div>
        </div>
        <div className="flex items-center flex-shrink-0">
          {membership.isCurrentOrganization && (
            <CheckIcon className="h-4 w-4 text-primary mr-1" />
          )}
          <Badge 
            text={getRoleDisplayName(membership.role)}
            variant={getRoleBadgeVariant(membership.role)}
            size="sm"
          />
        </div>
      </div>
    ),
    onClick: () => handleSwitchOrganization(membership.organization.id),
    disabled: membership.isCurrentOrganization || switchingOrganization,
    className: membership.isCurrentOrganization ? 'bg-gray-50' : ''
  }));

  // Add create new organization option
  const createOrganizationItem = {
    key: 'create-new',
    label: (
      <div className="flex items-center w-full min-w-0">
        <div className="w-6 h-6 bg-green-600 rounded flex items-center justify-center mr-3 flex-shrink-0">
          <PlusIcon className="h-4 w-4 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900">
            Create New Organization
          </p>
          <p className="text-xs text-gray-500">
            Set up a new organization
          </p>
        </div>
      </div>
    ),
    onClick: () => {
      // Handle click without triggering dropdown close automatically
      handleCreateNewOrganization();
    },
    disabled: switchingOrganization,
    className: 'hover:bg-green-50'
  };

  // Combine all dropdown items
  const dropdownItems = [
    ...organizationItems,
    createOrganizationItem
  ];

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <div className="w-8 h-8 rounded-lg bg-gray-200 animate-pulse"></div>
        </div>
        {!isCollapsed && (
          <div className="ml-3">
            <div className="h-4 bg-gray-200 rounded w-24 animate-pulse mb-1"></div>
            <div className="h-3 bg-gray-200 rounded w-16 animate-pulse"></div>
          </div>
        )}
      </div>
    );
  }

  // Show no organization state
  if (!organization || !role) {
    return (
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <div className="w-8 h-8 rounded-lg bg-gray-300 flex items-center justify-center">
            <span className="text-white font-semibold text-sm">?</span>
          </div>
        </div>
        {!isCollapsed && (
          <div className="ml-3">
            <p className="text-sm font-medium text-gray-400">No Organization</p>
            <p className="text-xs text-gray-400">Loading...</p>
          </div>
        )}
      </div>
    );
  }

  // Render the switcher
  const trigger = (
    <div 
      className={`flex items-center w-full cursor-pointer hover:bg-gray-50 rounded-lg p-2 transition-colors ${
        switchingOrganization ? 'opacity-50' : ''
      }`}
      onClick={toggleDropdown}
    >
      <div className="flex-shrink-0">
        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
          <span className="text-white font-semibold text-sm">
            {organization.displayName.charAt(0).toUpperCase()}
          </span>
        </div>
      </div>
      
      {!isCollapsed && (
        <div className="ml-3 flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1 mr-2">
              <p className="text-sm font-medium text-gray-900 truncate">
                {organization.displayName}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {organization.name}
              </p>
            </div>
            <div className="flex items-center flex-shrink-0">
              <Badge 
                text={getRoleDisplayName(role)}
                variant={getRoleBadgeVariant(role)}
                size="sm"
                className="mr-2"
              />
              {switchingOrganization ? (
                <LoadingSpinner size="small" />
              ) : (
                <ChevronDownIcon 
                  className={`h-4 w-4 text-gray-400 transition-transform ${
                    dropdownOpen ? 'rotate-180' : ''
                  }`} 
                />
              )}
            </div>
          </div>
        </div>
      )}
      
      {isCollapsed && !switchingOrganization && (
        <ChevronDownIcon 
          className={`h-3 w-3 text-gray-400 absolute -bottom-1 -right-1 bg-white rounded-full border transition-transform ${
            dropdownOpen ? 'rotate-180' : ''
          }`} 
        />
      )}
      
      {isCollapsed && switchingOrganization && (
        <div className="absolute -bottom-1 -right-1">
          <LoadingSpinner size="small" />
        </div>
      )}
    </div>
  );

  return (
    <div className="org-switcher-container">
      <Dropdown
        trigger={trigger}
        items={dropdownItems}
        isOpen={dropdownOpen}
        onClose={closeDropdown}
        placement="bottom-left"
        className="w-full"
      />
      
      {/* Create New Organization Modal */}
      {showCreateModal && (
        <CreateNewOrganizationModal
          onClose={handleModalClose}
          onSuccess={handleCreateSuccess}
        />
      )}
    </div>
  );
};

export default OrganizationSwitcher;